/**
 * Publishing an approved revision, and taking it back.
 *
 * Four async paths — preview, publish, withdraw, and simply opening the panel
 * — shared one `loading` flag and one `error` string inside a 776-line
 * component. Here they are one state machine, testable without a screen.
 *
 * @module components/portfolio/usePublication
 */

import { useCallback, useRef, useState } from "react";

import type { PortfolioProgrammeRevision, ProgrammePlatformClient } from "@/platform";
import type { ProgrammePublicationPreview } from "@/publication/types";
import { begin } from "@/utils/begin";

import { canBePublished } from "./revision-presentation";

type Published = Awaited<ReturnType<ProgrammePlatformClient["publishProgramme"]>>;

export interface PublicationPanel {
  revision: PortfolioProgrammeRevision;
  preview?: ProgrammePublicationPreview;
  /** `preview` is deciding whether to publish; `published` is managing one. */
  mode: "preview" | "published";
}

export interface UsePublicationResult {
  panel: PublicationPanel | null;
  busy: boolean;
  error: string | null;
  note: string;
  setNote: (note: string) => void;
  withdrawalReason: string;
  setWithdrawalReason: (reason: string) => void;
  /** Opens the panel, previewing first where publishing is the likely next step. */
  open: (revision: PortfolioProgrammeRevision) => void;
  close: () => void;
  publish: () => void;
  /** Resolves `true` once withdrawn, so a dialog asking for it knows to close. */
  withdraw: () => Promise<boolean>;
}

/** Fold a publication result back into the revision it belongs to. */
export function withPublication(
  revision: PortfolioProgrammeRevision,
  publication: Published,
): PortfolioProgrammeRevision {
  return {
    ...revision,
    latestPublication: {
      id: publication.id,
      publicProgrammeId: publication.publicProgrammeId,
      providerSlug: publication.payload.provider.providerSlug,
      publicationSequence: publication.publicationSequence,
      payloadHash: publication.payloadHash,
      publishedAt: publication.publishedAt,
      ...(publication.withdrawnAt ? { withdrawnAt: publication.withdrawnAt } : {}),
      ...(publication.withdrawalReason ? { withdrawalReason: publication.withdrawalReason } : {}),
    },
  };
}

/** Whether a revision currently has a live publication. */
export function isPublished(revision: PortfolioProgrammeRevision): boolean {
  return Boolean(revision.latestPublication && !revision.latestPublication.withdrawnAt);
}

export interface UsePublicationOptions {
  client: ProgrammePlatformClient;
  /** Whether the tenant's entitlement allows publishing at all. */
  canPublish: boolean;
  /** Told when a revision's publication changes, so the list can follow. */
  onRevisionChanged: (revision: PortfolioProgrammeRevision) => void;
}

/**
 * @example
 * ```tsx
 * const publication = usePublication({ client, canPublish, onRevisionChanged: replace });
 * ```
 */
export function usePublication({
  client,
  canPublish,
  onRevisionChanged,
}: UsePublicationOptions): UsePublicationResult {
  const [panel, setPanel] = useState<PublicationPanel | null>(null);
  const [busy, setBusy] = useState(false);
  // Every request that can write the panel back carries the value this counter
  // held when it started. Opening another revision, or closing, moves it on, so
  // a slower request landing afterwards cannot reopen a panel the user closed
  // or overwrite the revision they moved to. The same guard `useAsync` carries.
  const generation = useRef(0);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [withdrawalReason, setWithdrawalReason] = useState("");

  const message = (cause: unknown, fallback: string): string =>
    cause instanceof Error ? cause.message : fallback;

  const open = useCallback(
    (revision: PortfolioProgrammeRevision): void => {
      generation.current += 1;
      setError(null);
      setNote("");
      setWithdrawalReason("");

      // Previewing first only makes sense where publishing is the plausible
      // next step: an already-published revision, or one the entitlement
      // forbids publishing, opens straight into management.
      if (isPublished(revision) || !canBePublished(revision) || !canPublish) {
        setPanel({ revision, mode: "published" });
        setBusy(false);
        return;
      }

      setPanel({ revision, mode: "preview" });
      setBusy(true);
      const mine = generation.current;
      begin(() =>
        client.previewProgrammePublication({
          programmeId: revision.programmeId,
          curriculumRevisionId: revision.curriculumRevisionId,
        }),
      )
        .then((preview) => {
          if (generation.current !== mine) {
            return;
          }
          setPanel({ revision, preview, mode: "preview" });
        })
        .catch((cause: unknown) => {
          if (generation.current !== mine) {
            return;
          }
          setError(message(cause, "Unable to preview this publication."));
        })
        .finally(() => {
          if (generation.current === mine) {
            setBusy(false);
          }
        });
    },
    [canPublish, client],
  );

  const close = useCallback((): void => {
    generation.current += 1;
    setPanel(null);
    setError(null);
    setBusy(false);
  }, []);

  const publish = useCallback((): void => {
    const hash = panel?.preview?.payloadHash;
    if (!panel || !hash || !panel.preview?.readyToPublish) {
      return;
    }
    setBusy(true);
    setError(null);
    generation.current += 1;
    const mine = generation.current;
    begin(() =>
      client.publishProgramme({
        programmeId: panel.revision.programmeId,
        curriculumRevisionId: panel.revision.curriculumRevisionId,
        expectedPayloadHash: hash,
        ...(note.trim() ? { publicationNote: note.trim() } : {}),
      }),
    )
      .then((publication) => {
        // The list is told either way: the publication happened, whatever the
        // panel is showing by now. Only the panel is guarded.
        const revision = withPublication(panel.revision, publication);
        onRevisionChanged(revision);
        if (generation.current === mine) {
          setPanel({ revision, mode: "published" });
        }
      })
      .catch((cause: unknown) => {
        if (generation.current === mine) {
          setError(message(cause, "Unable to publish this programme."));
        }
      })
      .finally(() => {
        if (generation.current === mine) {
          setBusy(false);
        }
      });
  }, [client, note, onRevisionChanged, panel]);

  const withdraw = useCallback((): Promise<boolean> => {
    const publication = panel?.revision.latestPublication;
    const reason = withdrawalReason.trim();
    if (!reason) {
      setError("A withdrawal reason is required.");
      return Promise.resolve(false);
    }
    if (!panel || !publication) {
      return Promise.resolve(false);
    }
    setBusy(true);
    setError(null);
    generation.current += 1;
    const mine = generation.current;
    return begin(() =>
      client.withdrawProgrammePublication({
        programmeId: panel.revision.programmeId,
        publicationId: publication.id,
        reason,
      }),
    )
      .then((withdrawn) => {
        const revision = withPublication(panel.revision, withdrawn);
        onRevisionChanged(revision);
        if (generation.current === mine) {
          setPanel({ revision, mode: "published" });
        }
        return true;
      })
      .catch((cause: unknown) => {
        if (generation.current === mine) {
          setError(message(cause, "Unable to withdraw this publication."));
        }
        return false;
      })
      .finally(() => {
        if (generation.current === mine) {
          setBusy(false);
        }
      });
  }, [client, onRevisionChanged, panel, withdrawalReason]);

  return {
    panel,
    busy,
    error,
    note,
    setNote,
    withdrawalReason,
    setWithdrawalReason,
    open,
    close,
    publish,
    withdraw,
  };
}
